import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, AlertCircle, CreditCard, Settings2, Loader2 } from 'lucide-react';
import { useCreatePersonaSnapshotMutation, isInsufficientCreditsError } from '#store';
import { Routes } from '#Routes';

const DOC_COUNTS = [3, 5, 10];

/**
 * Modal: language, document count, POST to start generation; 402 → paywall.
 */
export function GeneratePersonaModal({
  show,
  onHide,
  ownerId,
  onGenerationStarted,
  defaultLanguage,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('pl') ? 'pl' : 'en';

  const [language, setLanguage] = useState(defaultLanguage === 'en' ? 'en' : 'pl');
  const [docCount, setDocCount] = useState(5);
  const [formError, setFormError] = useState(null);
  const [createSnapshot, { isLoading: isSubmitting }] = useCreatePersonaSnapshotMutation();

  useEffect(() => {
    if (show) {
      setFormError(null);
      setLanguage(defaultLanguage === 'en' ? 'en' : 'pl');
    }
  }, [show, defaultLanguage]);

  const creditsHref = Routes.AccountSubscribe.withLanguage(lang);

  const handleGenerate = useCallback(async () => {
    if (ownerId == null) {
      setFormError('general');
      return;
    }
    setFormError(null);
    try {
      await createSnapshot({
        ownerId,
        language,
        mode: 'full',
        maxDateColumns: 5,
        defaultRecentDocumentCount: docCount,
      }).unwrap();
      onGenerationStarted?.();
      onHide();
    } catch (err) {
      if (isInsufficientCreditsError(err)) {
        setFormError('credits');
      } else {
        setFormError('general');
      }
    }
  }, [ownerId, language, docCount, createSnapshot, onGenerationStarted, onHide]);

  if (formError === 'credits') {
    return (
      <Modal
        show={show}
        onHide={onHide}
        centered
        className='medical-persona-gen-modal'
        contentClassName='medical-persona-gen-modal__content'
        aria-labelledby='medical-persona-gen-modal-credits-title'
      >
        <Modal.Body className='medical-persona-gen-modal__body'>
          <div className='medical-persona-gen-modal__paywall'>
            <div className='medical-persona-gen-modal__paywall-icon' aria-hidden>
              <CreditCard strokeWidth={2} />
            </div>
            <h3
              id='medical-persona-gen-modal-credits-title'
              className='medical-persona-gen-modal__paywall-title'
            >
              {t('medical-persona.gen-modal-credits-title')}
            </h3>
            <p className='medical-persona-gen-modal__paywall-text'>
              {t('medical-persona.gen-modal-credits-body')}
            </p>
            <div className='medical-persona-gen-modal__paywall-actions'>
              <Button variant='outline-secondary' type='button' onClick={onHide}>
                {t('medical-persona.gen-modal-close')}
              </Button>
              <Button
                variant='success'
                className='medical-persona-gen-modal__paywall-cta'
                as={Link}
                to={creditsHref}
              >
                {t('medical-persona.gen-modal-buy-credits')}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className='medical-persona-gen-modal'
      contentClassName='medical-persona-gen-modal__content'
      aria-labelledby='medical-persona-gen-modal-title'
    >
      <Modal.Header closeButton>
        <Modal.Title
          id='medical-persona-gen-modal-title'
          className='d-flex align-items-center gap-2'
        >
          <span className='medical-persona-gen-modal__title-icon' aria-hidden>
            <Settings2 strokeWidth={2} />
          </span>
          {t('medical-persona.gen-modal-title')}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='medical-persona-gen-modal__body'>
        <div className='medical-persona-gen-modal__field'>
          <label className='medical-persona-gen-modal__label' htmlFor='persona-gen-lang'>
            {t('medical-persona.gen-modal-label-lang')}
          </label>
          <select
            id='persona-gen-lang'
            className='medical-persona-gen-modal__select'
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option value='pl'>{t('medical-persona.gen-modal-lang-pl')}</option>
            <option value='en'>{t('medical-persona.gen-modal-lang-en')}</option>
          </select>
        </div>
        <div className='medical-persona-gen-modal__field'>
          <label className='medical-persona-gen-modal__label' htmlFor='persona-gen-docs'>
            {t('medical-persona.gen-modal-label-docs')}
          </label>
          <select
            id='persona-gen-docs'
            className='medical-persona-gen-modal__select'
            value={docCount}
            onChange={(e) => setDocCount(Number(e.target.value))}
          >
            {DOC_COUNTS.map((n) => (
              <option key={n} value={n}>
                {t(`medical-persona.gen-modal-docs-${n}`)}
              </option>
            ))}
          </select>
        </div>
        <Alert variant='warning' className='medical-persona-gen-modal__consent-alert'>
          <Alert.Heading as='h6' className='medical-persona-gen-modal__consent-title'>
            {t('medical-persona.gen-modal-credits-consent-title')}
          </Alert.Heading>
          <p className='medical-persona-gen-modal__consent-text mb-2'>
            {t('medical-persona.gen-modal-credits-consent-p1')}
          </p>
          <p className='medical-persona-gen-modal__consent-text mb-0'>
            {t('medical-persona.gen-modal-credits-consent-p2')}
          </p>
        </Alert>
        {ownerId == null ? (
          <p className='medical-persona-gen-modal__warn' role='alert'>
            {t('medical-persona.gen-modal-no-owner')}
          </p>
        ) : null}
        {formError === 'general' ? (
          <div className='medical-persona-gen-modal__err' role='alert'>
            <AlertCircle
              className='medical-persona-icon medical-persona-gen-modal__err-icon'
              strokeWidth={2}
              aria-hidden
            />
            <span>{t('medical-persona.gen-modal-error-general')}</span>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer className='medical-persona-gen-modal__footer'>
        <Button variant='outline-secondary' type='button' onClick={onHide} disabled={isSubmitting}>
          {t('medical-persona.gen-modal-cancel')}
        </Button>
        <Button
          variant='success'
          type='button'
          className='medical-persona-gen-modal__submit'
          onClick={handleGenerate}
          disabled={isSubmitting || ownerId == null}
        >
          {isSubmitting ? (
            <Loader2 className='medical-persona-icon medical-persona-icon--spin' aria-hidden />
          ) : (
            <Sparkles className='medical-persona-icon' strokeWidth={2} aria-hidden />
          )}
          {t('medical-persona.gen-modal-submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
